import { Index } from 'flexsearch';
import type { NostrMetadata } from '@nostrify/nostrify';

export interface IndexedProfile {
  pubkey: string;
  metadata: NostrMetadata;
}

const STORAGE_KEY = 'zapcoffee_profile_index';
const MAX_CACHE_AGE = 24 * 60 * 60 * 1000; // 24 hours

/**
 * FlexSearch index for local profile searches
 */
class ProfileSearchIndex {
  private index: Index;
  private profiles: Map<string, IndexedProfile>;

  constructor() {
    this.index = new Index({
      tokenize: 'forward',
      charset: 'latin:extra',
      resolution: 3, // Lower resolution for faster searches
      context: true, // Enable context scoring for better relevance
    });
    this.profiles = new Map();

    // Try to load from localStorage
    this.loadFromStorage();
  }

  /**
   * Load profiles from localStorage
   */
  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return;

      const { profiles, timestamp } = JSON.parse(stored);

      // Check if cache is too old
      if (Date.now() - timestamp > MAX_CACHE_AGE) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      // Rebuild index from stored profiles
      profiles.forEach(({ pubkey, metadata }: IndexedProfile) => {
        this.add(pubkey, metadata);
      });
    } catch (error) {
      console.error('Failed to load profile index from storage:', error);
      localStorage.removeItem(STORAGE_KEY);
    }
  }

  /**
   * Save profiles to localStorage
   */
  private saveToStorage(): void {
    try {
      const profiles = Array.from(this.profiles.values());

      // Only store if we have profiles
      if (profiles.length === 0) return;

      // Limit storage size - keep only 500 most recently added profiles
      const toStore = profiles.slice(-500);

      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          profiles: toStore,
          timestamp: Date.now(),
        })
      );
    } catch (error) {
      console.error('Failed to save profile index to storage:', error);
      // Clear storage if we hit quota
      if (error instanceof DOMException && error.name === 'QuotaExceededError') {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }

  /**
   * Add a profile to the search index
   */
  add(pubkey: string, metadata: NostrMetadata): void {
    // Remove old entry if exists
    if (this.profiles.has(pubkey)) {
      this.index.remove(pubkey);
    }

    // Store profile
    this.profiles.set(pubkey, { pubkey, metadata });

    // Build searchable text from profile fields
    const searchableText = [
      metadata.display_name || '',
      metadata.name || '',
      metadata.nip05 || '',
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    // Add to index
    this.index.add(pubkey, searchableText);

    // Debounced save to localStorage (save every 10 profiles)
    if (this.profiles.size % 10 === 0) {
      this.saveToStorage();
    }
  }

  /**
   * Search the index for profiles matching query
   */
  search(query: string, limit = 5): IndexedProfile[] {
    // Early return for empty queries
    if (!query.trim()) return [];

    const results = this.index.search(query.toLowerCase(), {
      limit,
      suggest: true, // Enable fuzzy matching for better results
    });

    return results
      .map((id) => this.profiles.get(String(id)))
      .filter((profile): profile is IndexedProfile => profile !== undefined);
  }

  /**
   * Get a profile by pubkey
   */
  get(pubkey: string): IndexedProfile | undefined {
    return this.profiles.get(pubkey);
  }

  /**
   * Check if index has any profiles
   */
  get size(): number {
    return this.profiles.size;
  }

  /**
   * Clear the entire index
   */
  clear(): void {
    this.profiles.clear();
    // FlexSearch doesn't have a clear method, so we recreate the index
    this.index = new Index({
      tokenize: 'forward',
      charset: 'latin:extra',
      resolution: 3,
      context: true,
    });
  }
}

// Export singleton instance
export const profileSearchIndex = new ProfileSearchIndex();
